import { computed, ref, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";
import useMyFetch from "@stores/useMyFetch";
import { useMCampaignStore } from "@stores/mCampaign";

const CHANNEL_NAME = "m-campaign-design";
const POLL_INTERVAL_MS = 60_000;

interface GenerationMessage {
  mCampaignToken: string;
  designGeneration: string;
}

export const useMCampaignGenerationStore = defineStore("mCampaignGeneration", () => {
  const { currentMCampaign } = storeToRefs(useMCampaignStore());

  const mCampaignToken = computed<string | undefined>(() => currentMCampaign.value?.token);
  const generation = computed<string | undefined>(() => currentMCampaign.value?.design_generation);
  const isStale = ref(false);

  let channel: BroadcastChannel | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const isOtherGeneration = (token: string | undefined, designGeneration: string | undefined) =>
    !!generation.value && token === mCampaignToken.value && designGeneration !== generation.value;

  const refresh = async () => {
    if (!mCampaignToken.value || !generation.value || isStale.value) return;

    const token = mCampaignToken.value;
    const requestedGeneration = generation.value;
    const { response, data } = await useMyFetch(`/m_campaigns/${token}/design_generation`)
      .get()
      .json<{ design_generation: string }>();
    if (!response.value?.ok || generation.value !== requestedGeneration) return;
    if (isOtherGeneration(token, data.value?.design_generation)) isStale.value = true;
  };

  const handleMessage = (event: MessageEvent<GenerationMessage>) => {
    if (isOtherGeneration(event.data?.mCampaignToken, event.data?.designGeneration)) isStale.value = true;
  };

  watch([mCampaignToken, generation], ([token, designGeneration]) => {
    isStale.value = false;
    if (!designGeneration || !token) return;

    channel?.postMessage({ mCampaignToken: token, designGeneration } as GenerationMessage);
  });

  const revalidate = useDebounceFn(() => {
    if (document.visibilityState === "visible") refresh();
  }, 500);

  const start = () => {
    if (typeof BroadcastChannel !== "undefined" && !channel) {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.addEventListener("message", handleMessage);
    }
    document.addEventListener("visibilitychange", revalidate);
    window.addEventListener("focus", revalidate);
    pollTimer ??= setInterval(revalidate, POLL_INTERVAL_MS);
  };

  const stop = () => {
    channel?.removeEventListener("message", handleMessage);
    channel?.close();
    channel = null;
    document.removeEventListener("visibilitychange", revalidate);
    window.removeEventListener("focus", revalidate);
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
  };

  return { isStale, refresh, start, stop };
});
